/**
 * Session state. One store per domain — this one knows who is signed in and
 * nothing else.
 */
import { create } from 'zustand';
import { hasPermission, type AuthenticatedUser, type Permission } from '@genoma/shared';

import { fetchSession, logout as logoutRequest } from '../services/auth.service.ts';

type AuthState = {
  user: AuthenticatedUser | null;
  /** 'loading' until the first session check resolves, so guards do not flash. */
  status: 'loading' | 'authenticated' | 'anonymous';
  error: string | null;
  restore: () => Promise<void>;
  setUser: (user: AuthenticatedUser) => void;
  signOut: () => Promise<void>;
  can: (permission: Permission) => boolean;
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  status: 'loading',
  error: null,

  /** Called once on boot: the cookie may already hold a valid session. */
  restore: async () => {
    try {
      const session = await fetchSession();
      set({ user: session.user, status: 'authenticated', error: null });
    } catch {
      set({ user: null, status: 'anonymous' });
    }
  },

  setUser: (user) => set({ user, status: 'authenticated', error: null }),

  signOut: async () => {
    await logoutRequest().catch(() => undefined);
    set({ user: null, status: 'anonymous' });
  },

  can: (permission) => {
    const { user } = get();
    return user ? hasPermission(user.roles, permission) : false;
  },
}));


/**
 * Whether the signed-in person holds a permission.
 *
 * A hook rather than a selector call at each site, so a screen reads as
 * "can this person invite" instead of reaching into the store. It decides what
 * is worth rendering and nothing more — the API refuses regardless, and a
 * hidden button is not a security boundary.
 */
export const useCan = (permission: Permission): boolean =>
  useAuthStore((state) => state.can(permission));
